import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, RefreshCw, UploadCloud, LucideChevronRight, Info, Settings, Filter, BarChart3, Briefcase, Calculator } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { PortfolioPerformanceChart } from './PortfolioPerformanceChart';
import { StrategySelectorList } from './StrategySelectorList';
import { RiskOverview } from './RiskOverview';
import { RecentTradesTable } from './RecentTradesTable';
import { MonthlyReviewSection } from './MonthlyReviewSection';
import { AIInsightCard } from './AIInsightCard';
import { ExternalStrategyUploadModal } from './ExternalStrategyUploadModal';
import { DataProviderSelectionDialog, DataProviderId } from '../../src/components/dashboard/DataProviderSelectionDialog';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../src/integrations/supabase/client';
import {
  PortfolioSummaryData,
  StrategyPerformanceData,
  StrategyListItem,
  ExternalStrategyData,
  RiskMetric,
  TradeData,
  MonthlyPerformanceData,
  AIInsightData,
  EquityDataPoint,
  AllocationDataPoint,
} from './types';
import * as mockData from './mockData'; // Import mock data generator
import { processExternalStrategyTrades } from './metricCalculators';
import { useToast } from '../../src/hooks/use-toast';
import axios from 'axios';
import { vmTradingService } from '../../src/services/vmTradingService';
import { deploymentService } from '../../src/services/deploymentService';

// VM Trading Service types
interface VMAccountBalance {
  buying_power?: string | number;
  cash?: string | number;
  portfolio_value?: string | number;
  pattern_day_trader?: boolean;
  trading_blocked?: boolean;
  [key: string]: any;
}

interface VMPosition {
  symbol: string;
  qty?: string | number;
  market_value?: string | number;
  unrealized_pl?: string | number;
  cost_basis?: string | number;
  avg_entry_price?: string | number;
  [key: string]: any;
}

interface VMOrder {
  id: string;
  symbol: string;
  side: string;
  qty: string | number;
  status: string;
  created_at: string;
  filled_avg_price?: string | number;
  [key: string]: any;
}

// VM Trading Integration - No account ID needed as VM handles authentication

// Tax optimization types
interface TaxLot {
  id: string;
  symbol: string;
  quantity: number;
  purchaseDate: string;
  costBasis: number;
  currentValue: number;
}

interface TaxAllocation {
  symbol: string;
  taxableAccount: number; // percentage in taxable account
  taxDeferred: number;    // percentage in tax-deferred account
  taxFree: number;        // percentage in tax-free account
}

interface TaxEfficiencyMetrics {
  potentialLossHarvest: number;
  washSaleExposure: number;
  STCGExposure: number;
  LTCGExposure: number;
  assetLocationScore: number; // 0-100 score
  taxDragPercent: number;
}

interface TaxAlert {
  id: string;
  type: 'tax_loss_harvest' | 'wash_sale' | 'drift' | 'stcg_limit' | 'ltcg_limit' | 'asset_location';
  symbol?: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  potentialSavings?: number;
  recommendation?: string;
}

interface TaxConfig {
  maxSTCG: number;      // Maximum acceptable short-term capital gains (USD)
  maxLTCG: number;      // Maximum acceptable long-term capital gains (USD)
  doNotSellSymbols: string[]; // Securities to avoid selling
  harvestingActive: boolean;  // Whether automatic tax-loss harvesting is active
}

interface AccountType {
  id: string;
  name: string;
  type: 'taxable' | 'tax_deferred' | 'tax_free';
  value: number;
}

// Main component
export const PortfolioOverview: React.FC<{ strategyId?: string }> = ({ strategyId: initialStrategyId }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Initialize toast
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiInsightLoading, setAiInsightLoading] = useState<boolean>(false);
  
  // Strategy state management
  const [internalStrategies, setInternalStrategies] = useState<StrategyPerformanceData[]>([]);
  const [externalStrategies, setExternalStrategies] = useState<ExternalStrategyData[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(initialStrategyId || null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showTaxSettings, setShowTaxSettings] = useState(false);
  
  // Data provider selection state
  const [isProviderSelectionOpen, setIsProviderSelectionOpen] = useState(false);
  const [selectedDataProvider, setSelectedDataProvider] = useState<DataProviderId | null>(null);
  
  // Selected strategy data (to be displayed)
  const [selectedPortfolioSummary, setSelectedPortfolioSummary] = useState<PortfolioSummaryData | null>(null);
  const [selectedRiskMetrics, setSelectedRiskMetrics] = useState<RiskMetric[]>([]);
  const [selectedRecentTrades, setSelectedRecentTrades] = useState<TradeData[]>([]);
  const [selectedMonthlyPerformance, setSelectedMonthlyPerformance] = useState<MonthlyPerformanceData | null>(null);
  const [selectedAiInsight, setSelectedAiInsight] = useState<AIInsightData | null>(null);

  // Tax-related states
  const [taxLots, setTaxLots] = useState<TaxLot[]>([]);
  const [taxAlerts, setTaxAlerts] = useState<TaxAlert[]>([]);
  const [taxEfficiency, setTaxEfficiency] = useState<TaxEfficiencyMetrics | null>(null);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>({
    maxSTCG: 10000,
    maxLTCG: 40000,
    doNotSellSymbols: [],
    harvestingActive: true
  });
  const [accounts, setAccounts] = useState<AccountType[]>([]);
  const [optimalAllocation, setOptimalAllocation] = useState<TaxAllocation[]>([]);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  const [complianceCheckInProgress, setComplianceCheckInProgress] = useState(false);
  const [complianceResult, setComplianceResult] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // State for VM broker data - used to enhance strategy monitoring with real account data
  const [brokerBalance, setBrokerBalance] = useState<VMAccountBalance | null>(null);
  const [brokerPositions, setBrokerPositions] = useState<VMPosition[]>([]);
  const [strategyOrders, setStrategyOrders] = useState<VMOrder[]>([]);

  // 🎯 PERSISTENT STRATEGY STATE TRACKING - prevent flip-flopping of hasStartedTrading
  const [strategyTradingStates, setStrategyTradingStates] = useState<Record<string, boolean>>({});

  // Helper function to safely parse numeric values from VM API - fixed to handle 0 values correctly and thousands separators
  const safeParseFloat = (value: string | number | undefined | null, defaultValue: number = 0): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remove commas and other thousands separators
      const cleanValue = value.replace(/,/g, '');
      const parsed = parseFloat(cleanValue);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    return defaultValue;
  };

  // Combined list of strategies for display
  const allStrategies = useMemo(() => {
    return [
      ...internalStrategies.map(strategy => ({
        ...strategy,
        source: 'internal' as const,
      })),
      ...externalStrategies.map(strategy => ({
        ...strategy,
        source: 'external' as const,
      }))
    ].sort((a, b) => {
      // Sort by profitability (ytdReturn) in descending order, then by source
      if (a.ytdReturn !== undefined && b.ytdReturn !== undefined) {
        // If both have ytdReturn values, sort by profitability
        return b.ytdReturn - a.ytdReturn;
      } else if (a.ytdReturn !== undefined) {
        // If only a has ytdReturn, prioritize a
        return -1;
      } else if (b.ytdReturn !== undefined) {
        // If only b has ytdReturn, prioritize b
        return 1;
      }
      
      // If neither has ytdReturn, sort by source (internal first), then name
      if (a.source !== b.source) return a.source === 'internal' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [internalStrategies, externalStrategies]);

  // VM authentication is handled internally, no account ID validation needed

  // Fetch VM broker data to enhance strategy monitoring with real account data
  const fetchBrokerData = async (): Promise<void> => {
    try {
      console.log(`[PortfolioOverview] Fetching broker data from VM trading service...`);

      // Try to fetch data from VM trading service
      const [vmAccount, vmPositions, vmOrders] = await Promise.all([
        vmTradingService.getAccount().catch(err => {
          console.warn('[PortfolioOverview] VM account fetch failed:', err.message);
          return null;
        }),
        vmTradingService.getPositions().catch(err => {
          console.warn('[PortfolioOverview] VM positions fetch failed:', err.message);
          return [];
        }),
        vmTradingService.getOrders().catch(err => {
          console.warn('[PortfolioOverview] VM orders fetch failed:', err.message);
          return [];
        })
      ]);

      // Transform VM account data to expected format
      const transformedAccount: VMAccountBalance | null = vmAccount ? {
        buying_power: vmAccount.buying_power || vmAccount.cash || 0,
        cash: vmAccount.cash || 0,
        portfolio_value: vmAccount.portfolio_value || 0,
        pattern_day_trader: vmAccount.pattern_day_trader || false,
        trading_blocked: vmAccount.trading_blocked || false,
        ...vmAccount
      } : null;

      // Transform VM positions (they should already be in the right format)
      const transformedPositions: VMPosition[] = Array.isArray(vmPositions) ? vmPositions : [];
      
      // Transform VM orders (they should already be in the right format)  
      const transformedOrders: VMOrder[] = Array.isArray(vmOrders) ? vmOrders : [];

      setBrokerBalance(transformedAccount);
      setBrokerPositions(transformedPositions);
      setStrategyOrders(transformedOrders);

      console.log(`[PortfolioOverview] VM data loaded: account=${!!transformedAccount}, ${transformedPositions.length} positions, ${transformedOrders.length} orders`);
    } catch (error: any) {
      console.error('[PortfolioOverview] Failed to fetch VM broker data:', error);
      
      // Show user-friendly error message
      toast({
        title: "VM Connection Error", 
        description: "Failed to fetch live account data from VM. Using simulation data as fallback.",
        variant: "destructive",
      });
      
      // Clear any existing data
      setBrokerBalance(null);
      setBrokerPositions([]);
      setStrategyOrders([]);
    }
  };

  // Smart data source selection - Broker API first for live strategies
  const getPortfolioDataForStrategy = async (strategyId: string, source: 'internal' | 'external') => {
    console.log(`[PortfolioOverview] Getting portfolio data for strategy: ${strategyId}, source: ${source}`);
    
    if (source === 'internal') {
      // Find the selected strategy to determine its mode
      const selectedStrategy = internalStrategies.find(s => s.id === strategyId);
      
      // 🎯 ENHANCED STRATEGY MODE DETECTION for real VM strategies
      let isLiveStrategy = false;
      let strategyMode = 'backtest';
      
      if (selectedStrategy) {
        // Check multiple possible fields for live strategy detection
        const vmStrategy = (selectedStrategy as any).vmStrategy;
        const backtestSettings = (selectedStrategy as any).backtestSettings;
        
        // Priority order: VM strategy trading_mode > backtestSettings.mode > strategy status
        if (vmStrategy?.trading_mode) {
          strategyMode = vmStrategy.trading_mode;
          isLiveStrategy = vmStrategy.trading_mode === 'live';
        } else if (backtestSettings?.mode) {
          strategyMode = backtestSettings.mode;
          isLiveStrategy = backtestSettings.mode === 'live';
        } else if (vmStrategy?.status === 'running' && selectedStrategy.status === 'active') {
          // If it's a running VM strategy, assume it's live
          strategyMode = 'live';
          isLiveStrategy = true;
        }
      }
      
      console.log(`[PortfolioOverview] Strategy mode: ${strategyMode}, isLive: ${isLiveStrategy}`);
      
      // 🎯 CHECK IF STRATEGY HAS ACTUALLY STARTED TRADING
      const hasStartedTrading = (selectedStrategy as any)?.backtestSettings?.hasStartedTrading;
      const vmStrategy = (selectedStrategy as any)?.vmStrategy;
      const isVMStrategy = !!vmStrategy;
      
      console.log(`[PortfolioOverview] 🔍 STRATEGY ANALYSIS for ${strategyId}:`, {
        name: selectedStrategy?.name,
        hasStartedTrading,
        isVMStrategy,
        isLiveStrategy,
        persistentState: strategyTradingStates[strategyId],
        vmData: vmStrategy ? {
          active_positions: vmStrategy.active_positions,
          total_pnl: vmStrategy.total_pnl,
          total_trades: vmStrategy.total_trades,
          executed_orders: vmStrategy.executed_orders,
          status: vmStrategy.status
        } : null,
        brokerData: {
          hasBalance: !!brokerBalance,
          positionsCount: brokerPositions.length,
          ordersCount: strategyOrders.length
        }
      });
      
      if (isLiveStrategy && hasStartedTrading && brokerBalance && brokerPositions.length > 0) {
        // 🎯 DEPLOYED STRATEGY WITH TRADES: Use strategy-specific broker data
        console.log(`[PortfolioOverview] Using STRATEGY-SPECIFIC broker data for trading strategy: ${strategyId}`);
        
        // Filter positions that belong to this specific strategy
        const strategyPositions = brokerPositions.filter(pos => 
          pos.strategy_id === strategyId || 
          pos.strategy_name === selectedStrategy?.name ||
          (vmStrategy?.symbols && vmStrategy.symbols.includes(pos.symbol))
        );
        
        console.log(`[PortfolioOverview] Found ${strategyPositions.length} positions for strategy ${strategyId}:`, strategyPositions.map(p => p.symbol));
        
        if (strategyPositions.length === 0) {
          console.log(`[PortfolioOverview] No strategy-specific positions found, showing empty state`);
          // Even though it's marked as "started trading", no positions found - show empty state
          return {
            portfolioSummary: {
              totalValue: 0,
              todayChangeValue: 0,
              todayChangePercent: 0,
              equityCurve: [],
              allocation: []
            },
            riskMetrics: [],
            recentTrades: [],
            monthlyPerformance: {
              month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
              returnPercent: 0,
              aiCommentary: "Strategy positions not found in current portfolio."
            },
            dataSource: 'empty' as const
          };
        }
        
        const realPortfolioValue = strategyPositions.reduce((total, pos) => {
          return total + safeParseFloat(pos.market_value, 0);
        }, 0);

        const todaysChangeValue = strategyPositions.reduce((total, pos) => {
          return total + safeParseFloat(pos.unrealized_pl, 0);
        }, 0);

        // Map STRATEGY positions to allocation format (not all broker positions)
        const allocation: AllocationDataPoint[] = strategyPositions.map((pos, index) => ({
          name: pos.symbol,
          value: realPortfolioValue > 0 ? (safeParseFloat(pos.market_value, 0) / realPortfolioValue) * 100 : 0,
          color: `hsl(${index * 137.5 % 360}, 70%, 50%)` // Generate colors
        }));

        const portfolioSummary: PortfolioSummaryData = {
          totalValue: realPortfolioValue,
          todayChangeValue: todaysChangeValue,
          todayChangePercent: realPortfolioValue > 0 ? (todaysChangeValue / realPortfolioValue) * 100 : 0,
          equityCurve: [], // TODO: Could be populated from historical broker data
          allocation: allocation
        };

        // Get STRATEGY-SPECIFIC recent trades from broker orders
        const strategyTrades = (strategyOrders ?? [])
          .filter(order => 
            (order.status === 'filled' || order.status === 'executed') &&
            (order.strategy_id === strategyId || 
             order.strategy_name === selectedStrategy?.name ||
             (vmStrategy?.symbols && vmStrategy.symbols.includes(order.symbol)))
          );
          
        const recentTrades: TradeData[] = strategyTrades
          .slice(0, 8)
          .map(order => ({
            id: order.id,
            symbol: order.symbol,
            action: (order.side === 'buy' || order.side === 'sell') ? order.side : 'buy',
            quantity: safeParseFloat(order.qty, 0),
            price: safeParseFloat(order.filled_avg_price, 0),
            timestamp: order.created_at,
            status: order.status,
            strategyId: strategyId,
            strategyName: selectedStrategy?.name || 'Live Strategy'
          }));
          
        console.log(`[PortfolioOverview] Found ${strategyTrades.length} strategy-specific trades for ${strategyId}`);

        // Generate risk metrics based on STRATEGY-SPECIFIC positions
        const riskMetrics: RiskMetric[] = [
          {
            id: 'strategy-drawdown',
            name: 'Strategy Drawdown',
            value: Math.abs(todaysChangeValue) > 0 ? ((Math.abs(todaysChangeValue) / realPortfolioValue) * 100).toFixed(1) + '%' : '0.0%',
            level: Math.abs(todaysChangeValue / realPortfolioValue) > 0.05 ? 'high' : 'low'
          },
          {
            id: 'strategy-value',
            name: 'Strategy Value',
            value: `$${realPortfolioValue.toLocaleString()}`,
            level: 'info'
          },
          {
            id: 'strategy-positions',
            name: 'Strategy Positions',
            value: strategyPositions.length.toString(),
            level: 'info'
          },
          {
            id: 'strategy-pnl',
            name: 'Strategy P&L',
            value: `$${todaysChangeValue.toFixed(2)}`,
            level: todaysChangeValue >= 0 ? 'low' : 'medium'
          }
        ];

        return {
          portfolioSummary,
          riskMetrics,
          recentTrades,
          monthlyPerformance: mockData.generateMonthlyPerformance(), // TODO: Calculate from real data
          dataSource: 'broker' as const
        };
              } else if (isLiveStrategy && !hasStartedTrading) {
          // 🎯 DEPLOYED STRATEGY THAT HASN'T STARTED TRADING YET - Show empty states
          console.log(`[PortfolioOverview] 🎯 DEPLOYED STRATEGY - EMPTY STATE for ${strategyId}:`, {
            name: selectedStrategy?.name,
            reason: 'Strategy deployed but not trading yet',
            isLiveStrategy,
            hasStartedTrading,
            persistentState: strategyTradingStates[strategyId]
          });
        
        return {
          portfolioSummary: {
            totalValue: 0,
            todayChangeValue: 0,
            todayChangePercent: 0,
            equityCurve: [],
            allocation: []
          },
          riskMetrics: [],
          recentTrades: [],
          monthlyPerformance: {
            month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
            returnPercent: 0,
            aiCommentary: "Strategy is deployed and ready to trade. Performance data will appear once trading begins."
          },
          dataSource: 'empty' as const
        };
                      } else if (isVMStrategy && !hasStartedTrading) {
          // 🎯 VM STRATEGY NOT TRADING - Force empty state (failsafe for consistency)
          console.log(`[PortfolioOverview] 🎯 VM STRATEGY FAILSAFE - EMPTY STATE for ${strategyId}:`, {
            name: selectedStrategy?.name,
            reason: 'VM strategy failsafe - ensuring consistent empty state',
            isLiveStrategy,
            hasStartedTrading,
            persistentState: strategyTradingStates[strategyId]
          });
          
          return {
            portfolioSummary: {
              totalValue: 0,
              todayChangeValue: 0,
              todayChangePercent: 0,
              equityCurve: [],
              allocation: []
            },
            riskMetrics: [],
            recentTrades: [],
            monthlyPerformance: {
              month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
              returnPercent: 0,
              aiCommentary: "Strategy is deployed and monitoring markets. Performance data will appear once trading begins."
            },
            dataSource: 'empty' as const
          };
        } else {
          // 📊 DEMO/BACKTEST STRATEGY: Use simulation data
          console.log(`[PortfolioOverview] 📊 SIMULATION DATA for ${strategyId}:`, {
            name: selectedStrategy?.name,
            reason: 'Demo/backtest strategy',
            isVMStrategy,
            strategyMode: strategyMode || 'demo'
          });
          
          const portfolioSummary = mockData.generatePortfolioSummary();
          const riskMetrics = mockData.generateRiskMetrics();
          const recentTrades = mockData.generateRecentTrades(8);
          const monthlyPerformance = mockData.generateMonthlyPerformance();

          return {
            portfolioSummary,
            riskMetrics,
            recentTrades,
            monthlyPerformance,
            dataSource: 'simulation' as const
          };
        }
    } else {
      // External strategies always use their processed data
      const externalStrategy = externalStrategies.find(s => s.id === strategyId);
      
      if (externalStrategy) {
        if (externalStrategy.status === 'pending_analysis') {
          return {
            portfolioSummary: {
              totalValue: 0,
              todayChangeValue: 0,
              todayChangePercent: 0,
              equityCurve: [{ date: new Date().toISOString().split('T')[0], value: 0 }],
              allocation: []
            },
            riskMetrics: [],
            recentTrades: [],
            monthlyPerformance: null,
            dataSource: 'external' as const
          };
        } else if (externalStrategy.trades?.length) {
          await new Promise(resolve => setTimeout(resolve, 800));
          
          const processResult = processExternalStrategyTrades(externalStrategy.trades, 100000);
          
          return {
            portfolioSummary: {
              totalValue: processResult.totalValue || 0,
              todayChangeValue: processResult.todayChangeValue || 0,
              todayChangePercent: processResult.todayChangePercent || 0,
              equityCurve: processResult.equityCurve || [],
              allocation: processResult.allocation || []
            },
            riskMetrics: processResult.riskMetrics || [],
            recentTrades: processResult.recentTrades || [],
            monthlyPerformance: processResult.monthlyPerformance || null,
            dataSource: 'external' as const
          };
        }
      }
      
      // Fallback for external strategies with no data
      return {
        portfolioSummary: null,
        riskMetrics: [],
        recentTrades: [],
        monthlyPerformance: null,
        dataSource: 'external' as const
      };
    }
  };

  // Fetch core portfolio data
  const fetchData = useCallback(async (showLoading = true) => {
    if(showLoading) setIsLoading(true);
    setError(null);
    console.log("PortfolioOverview: Fetching data...");

    try {
      // Fetch both real strategies and broker data in parallel
      await Promise.all([
        // Existing mock data fetch with simulated delay
        new Promise(resolve => setTimeout(resolve, showLoading ? 1500 : 500)),
        // Real broker data fetch - await this to prevent race condition
        fetchBrokerData()
      ]);

      // 🎯 PRIORITIZE DEPLOYED STRATEGIES FROM VM
      let strategies: StrategyPerformanceData[] = [];
      let hasRealStrategies = false;

      try {
        // Import deployment service dynamically
        const { deploymentService } = await import('../../src/services/deploymentService');
        
        console.log("PortfolioOverview: Attempting to fetch deployed strategies from VM...");
        const realStrategiesResponse = await deploymentService.listDeployedStrategies();
        
        if (realStrategiesResponse?.success && realStrategiesResponse.deployed_strategies) {
          const realStrategies = realStrategiesResponse.deployed_strategies;
          console.log("PortfolioOverview: Found deployed strategies:", realStrategies.length);
          
          // Transform real VM strategies to PortfolioOverview format - ALWAYS include them
          const newStrategyStates: Record<string, boolean> = {};
          
          strategies = realStrategies.map((vmStrategy: any) => {
            const strategyId = vmStrategy.strategy_id || vmStrategy.id;
            
            // 🎯 DETERMINE if strategy has started trading with multiple indicators
            const currentlyTrading = vmStrategy.has_started_trading !== undefined ? 
              vmStrategy.has_started_trading : 
              ((vmStrategy.active_positions || 0) > 0 || 
               (vmStrategy.total_pnl || 0) !== 0 || 
               (vmStrategy.total_trades || 0) > 0 ||
               (vmStrategy.executed_orders || 0) > 0);
            
            // 🎯 PERSISTENT STATE - once trading, always trading (prevent flip-flopping)
            const persistentlyTrading = strategyTradingStates[strategyId] || currentlyTrading;
            newStrategyStates[strategyId] = persistentlyTrading;
            
            console.log(`[PortfolioOverview] Strategy ${strategyId}:`, {
              name: vmStrategy.name,
              currentlyTrading,
              persistentlyTrading,
              wasTrackedBefore: strategyId in strategyTradingStates,
              active_positions: vmStrategy.active_positions,
              total_pnl: vmStrategy.total_pnl,
              total_trades: vmStrategy.total_trades,
              executed_orders: vmStrategy.executed_orders
            });

            return {
              id: strategyId,
              name: vmStrategy.name || `Strategy ${strategyId}`,
              source: 'internal' as const,
              description: `${vmStrategy.trading_mode || 'Live'} strategy ${persistentlyTrading ? '(Trading)' : '(Deployed)'}${vmStrategy.symbols?.length ? ` - ${vmStrategy.symbols.length} symbols` : ''}`,
              ytdReturn: vmStrategy.total_pnl || 0, // Real P&L or 0 if no trades yet
              status: vmStrategy.status === 'active' ? 'active' : 'inactive',
              riskLevel: 'medium' as const,
              // Include VM-specific data for mode detection
              backtestSettings: {
                mode: vmStrategy.trading_mode?.toLowerCase() || 'live',
                symbols: vmStrategy.symbols || [],
                hasStartedTrading: persistentlyTrading
              },
              // Store full VM strategy data for detailed analysis
              vmStrategy: vmStrategy
            };
          });
          
          // 🎯 UPDATE persistent strategy states
          setStrategyTradingStates(prev => ({ ...prev, ...newStrategyStates }));
          
          hasRealStrategies = strategies.length > 0;
          console.log("PortfolioOverview: Deployed strategies available:", hasRealStrategies);
          
          // Log which strategies have started trading vs not
          const tradingStrategies = strategies.filter(s => (s as any).backtestSettings?.hasStartedTrading).length;
          const pendingStrategies = strategies.length - tradingStrategies;
          console.log(`PortfolioOverview: ${tradingStrategies} trading, ${pendingStrategies} pending trades`);
        }
      } catch (realStrategyError) {
        console.warn("PortfolioOverview: Failed to fetch deployed strategies:", realStrategyError);
        // Don't toast errors during normal operation
      }

      // 📊 ADD DEMO STRATEGIES IF NEEDED (but prioritize real ones)
      if (!hasRealStrategies) {
        console.log("PortfolioOverview: No deployed strategies found, adding demo strategies");
        strategies = mockData.generateTopStrategies(3);
      } else {
        // If we have real strategies, add just one demo strategy for comparison
        console.log("PortfolioOverview: Adding demo strategy alongside deployed strategies");
        const demoStrategies = mockData.generateTopStrategies(1);
        demoStrategies[0].name = "Demo Strategy (Sample)";
        demoStrategies[0].description = "Sample strategy for comparison";
        strategies.push(...demoStrategies);
      }
      
      // Set portfolio summary based on strategy type
      let portfolioSummary;
      if (hasRealStrategies && brokerBalance) {
        // Use real broker data for portfolio summary when we have real strategies
        const realPortfolioValue = Number(brokerBalance.portfolio_value || 0);
        const realCash = Number(brokerBalance.cash || 0);
        portfolioSummary = {
          totalValue: realPortfolioValue,
          todayChangeValue: 0, // TODO: Calculate from positions
          todayChangePercent: 0,
          equityCurve: [{ date: new Date().toISOString().split('T')[0], value: realPortfolioValue }],
          allocation: [] // TODO: Calculate from positions
        };
      } else {
        // Use mock data for demo portfolio
        portfolioSummary = mockData.generatePortfolioSummary();
      }
      
      // Update state with fetched data
      setInternalStrategies(strategies);
      
      // Handle strategy selection
      if (!selectedStrategyId && strategies.length > 0) {
        const firstStrategyId = strategies[0].id;
        setSelectedStrategyId(firstStrategyId);
        handleStrategySelect(firstStrategyId, 'internal');
      } else if (selectedStrategyId) {
        // If a strategy is already selected, refresh its data
        const selectedStrategy = strategies.find(s => s.id === selectedStrategyId);
        if (selectedStrategy) {
          // 🎯 SMART DATA SELECTION: Real strategies vs Demo strategies vs Empty states
          const isVMStrategy = selectedStrategy.name !== "Demo Strategy (Sample)";
                const hasStartedTrading = (selectedStrategy as any).backtestSettings?.hasStartedTrading;

      console.log(`[PortfolioOverview] 🔄 USEEFFECT STRATEGY UPDATE for ${selectedStrategyId}:`, {
        strategyName: selectedStrategy.name,
        isVMStrategy,
        hasStartedTrading,
        persistentState: strategyTradingStates[selectedStrategyId],
        backtestSettings: (selectedStrategy as any).backtestSettings
      });

      if (isVMStrategy && !hasStartedTrading) {
            // 📊 DEPLOYED STRATEGY THAT HASN'T STARTED TRADING YET - Show empty states
            console.log("PortfolioOverview: Using empty states for pending strategy");
            setSelectedPortfolioSummary({
              totalValue: 0,
              todayChangeValue: 0,
              todayChangePercent: 0,
              equityCurve: [],
              allocation: []
            });
            setSelectedRiskMetrics([]);
            setSelectedRecentTrades([]);
            setSelectedMonthlyPerformance({
              month: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
              returnPercent: 0,
              benchmarkReturnPercent: 0,
              keyEvents: [],
              aiCommentary: "Strategy is waiting to begin trading."
            });
            setSelectedAiInsight({
              id: 'pending-strategy',
              timestamp: new Date().toISOString(),
              category: 'Portfolio',
              text: `${selectedStrategy.name} is deployed and ready to trade. Performance data will appear here once trading begins.`,
              severity: 'info'
            });
          } else if (isVMStrategy && hasStartedTrading) {
            // 📊 DEPLOYED STRATEGY WITH TRADES - Use real data
            console.log("PortfolioOverview: Using real data for active trading strategy");
            const portfolioData = await getPortfolioDataForStrategy(selectedStrategyId, 'internal');
            setSelectedPortfolioSummary(portfolioData.portfolioSummary);
            setSelectedRiskMetrics(portfolioData.riskMetrics);
            setSelectedRecentTrades(portfolioData.recentTrades);
            setSelectedMonthlyPerformance(portfolioData.monthlyPerformance);
            setSelectedAiInsight(mockData.generateAIInsight());
          } else {
            // 📊 DEMO STRATEGY - Use mock data
            console.log("PortfolioOverview: Using mock data for demo strategy");
            setSelectedPortfolioSummary(portfolioSummary);
            setSelectedRiskMetrics(mockData.generateRiskMetrics());
            setSelectedRecentTrades(mockData.generateRecentTrades(8));
            setSelectedMonthlyPerformance(mockData.generateMonthlyPerformance());
            setSelectedAiInsight(mockData.generateAIInsight());
          }
          
          // Generate tax-related data
          generateTaxData();
        }
      } else {
        // No strategy to select, show overall portfolio
        setSelectedPortfolioSummary(portfolioSummary);
        setSelectedRiskMetrics(mockData.generateRiskMetrics());
        setSelectedRecentTrades(mockData.generateRecentTrades(8));
        setSelectedMonthlyPerformance(mockData.generateMonthlyPerformance());
        setSelectedAiInsight(mockData.generateAIInsight());
        
        // Generate tax-related data
        generateTaxData();
      }

      console.log(`PortfolioOverview: Data fetched successfully. Real strategies: ${hasRealStrategies}`);
    } catch (err: any) {
      console.error("PortfolioOverview: Error fetching data -", err);
      setError(err.message || "An unexpected error occurred.");
      // Clear data on error
      setSelectedPortfolioSummary(null);
      setInternalStrategies([]);
      setSelectedRiskMetrics([]);
      setSelectedRecentTrades([]);
      setSelectedMonthlyPerformance(null);
      setSelectedAiInsight(null);
      setTaxLots([]);
      setTaxAlerts([]);
      setTaxEfficiency(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Generate tax-related data - enhanced to use real data when available
  const generateTaxData = () => {
    // 🎯 USE REAL TAX DATA when we have real trades
    const hasRealTrades = selectedRecentTrades.length > 0 && 
                          selectedRecentTrades.some(trade => trade.strategyId && trade.strategyName !== undefined);
    
    if (hasRealTrades) {
      console.log("PortfolioOverview: Generating tax data from REAL trades");
      
      // Generate tax lots from real recent trades
      const realTaxLots: TaxLot[] = [];
      const tradesBySymbol = selectedRecentTrades.reduce((acc, trade) => {
        if (!acc[trade.symbol]) acc[trade.symbol] = [];
        acc[trade.symbol].push(trade);
        return acc;
      }, {} as Record<string, typeof selectedRecentTrades>);
      
      Object.entries(tradesBySymbol).forEach(([symbol, trades]) => {
        // Group by buy transactions to create tax lots
        const buyTrades = trades.filter(t => t.action === 'buy');
        
        buyTrades.forEach((buyTrade, index) => {
          const purchaseDate = new Date(buyTrade.timestamp).toISOString().split('T')[0];
          const daysSincePurchase = Math.floor((new Date().getTime() - new Date(buyTrade.timestamp).getTime()) / (1000 * 60 * 60 * 24));
          
          // Estimate current value (in real implementation, would fetch current market price)
          const estimatedCurrentPrice = buyTrade.price * (0.95 + Math.random() * 0.1); // ±5% from purchase price
          const currentValue = estimatedCurrentPrice * buyTrade.quantity;
          
          realTaxLots.push({
            id: `real_lot_${symbol}_${index}`,
            symbol,
            quantity: buyTrade.quantity,
            purchaseDate,
            costBasis: buyTrade.price,
            currentValue: parseFloat(currentValue.toFixed(2))
          });
        });
      });
      
      setTaxLots(realTaxLots);
      
      // Calculate real tax efficiency metrics from actual trades
      const totalCostBasis = realTaxLots.reduce((sum, lot) => sum + (lot.costBasis * lot.quantity), 0);
      const totalCurrentValue = realTaxLots.reduce((sum, lot) => sum + lot.currentValue, 0);
      const totalGainLoss = totalCurrentValue - totalCostBasis;
      
      // Separate short-term vs long-term based on purchase dates
      const shortTermLots = realTaxLots.filter(lot => {
        const daysSince = Math.floor((new Date().getTime() - new Date(lot.purchaseDate).getTime()) / (1000 * 60 * 60 * 24));
        return daysSince <= 365;
      });
      
      const longTermLots = realTaxLots.filter(lot => {
        const daysSince = Math.floor((new Date().getTime() - new Date(lot.purchaseDate).getTime()) / (1000 * 60 * 60 * 24));
        return daysSince > 365;
      });
      
      const shortTermGainLoss = shortTermLots.reduce((sum, lot) => sum + (lot.currentValue - (lot.costBasis * lot.quantity)), 0);
      const longTermGainLoss = longTermLots.reduce((sum, lot) => sum + (lot.currentValue - (lot.costBasis * lot.quantity)), 0);
      
      const realTaxEfficiency: TaxEfficiencyMetrics = {
        potentialLossHarvest: Math.max(0, -Math.min(shortTermGainLoss, longTermGainLoss)),
        washSaleExposure: 0, // Would need to check for wash sale violations
        STCGExposure: Math.max(0, shortTermGainLoss),
        LTCGExposure: Math.max(0, longTermGainLoss),
        assetLocationScore: 85, // Assume good asset location for real strategies
        taxDragPercent: Math.abs(totalGainLoss) / Math.max(totalCurrentValue, 1) * 0.3 // Estimated tax drag
      };
      
      setTaxEfficiency(realTaxEfficiency);
      
      // Generate real tax alerts based on actual positions
      const realAlerts: TaxAlert[] = [];
      
      // Check for actual tax-loss harvesting opportunities
      const losingPositions = realTaxLots.filter(lot => lot.currentValue < (lot.costBasis * lot.quantity));
      if (losingPositions.length > 0) {
        const totalLosses = losingPositions.reduce((sum, lot) => sum + ((lot.costBasis * lot.quantity) - lot.currentValue), 0);
        if (totalLosses > 1000) {
          realAlerts.push({
            id: 'real_tlh_alert',
            type: 'tax_loss_harvest',
            symbol: losingPositions[0].symbol,
            severity: 'medium',
            message: `Tax-loss harvesting opportunity detected - potential savings of $${(totalLosses * 0.35).toFixed(0)}.`,
            potentialSavings: totalLosses * 0.35,
            recommendation: `Consider harvesting losses in ${losingPositions.map(p => p.symbol).join(', ')} to offset gains.`
          });
        }
      }
      
      // Check STCG limits
      if (realTaxEfficiency.STCGExposure > taxConfig.maxSTCG * 0.8) {
        realAlerts.push({
          id: 'real_stcg_alert',
          type: 'stcg_limit',
          severity: realTaxEfficiency.STCGExposure > taxConfig.maxSTCG ? 'high' : 'medium',
          message: `Short-term capital gains: $${realTaxEfficiency.STCGExposure.toFixed(0)} of $${taxConfig.maxSTCG.toLocaleString()} limit.`,
          recommendation: `Consider holding positions longer to qualify for long-term treatment.`
        });
      }
      
      setTaxAlerts(realAlerts);
      
      // Generate optimal allocation from real positions
      const realOptimalAllocation: TaxAllocation[] = realTaxLots.map(lot => ({
        symbol: lot.symbol,
        taxableAccount: 70, // Most positions in taxable for active trading
        taxDeferred: 20,
        taxFree: 10
      }));
      
      setOptimalAllocation(realOptimalAllocation);
      
    } else {
      // 📊 FALLBACK TO MOCK TAX DATA for demo/backtest strategies
      console.log("PortfolioOverview: Generating MOCK tax data");
      
    // Mock account types
    const mockAccounts: AccountType[] = [
      { id: 'acc1', name: 'Taxable Brokerage', type: 'taxable', value: 650000 },
      { id: 'acc2', name: 'Traditional IRA', type: 'tax_deferred', value: 420000 },
      { id: 'acc3', name: 'Roth IRA', type: 'tax_free', value: 380000 }
    ];
    setAccounts(mockAccounts);

    // Generate mock tax lots based on trades
    const mockTaxLots: TaxLot[] = [];
    const symbols = [...new Set(selectedRecentTrades.map(trade => trade.symbol))];
    
    // Create a mix of short-term and long-term lots for each symbol
    symbols.forEach(symbol => {
      const lotsCount = Math.floor(Math.random() * 4) + 1; // 1-4 lots per symbol
      
      for (let i = 0; i < lotsCount; i++) {
        const isLongTerm = Math.random() > 0.4; // 60% chance of long-term
        const daysAgo = isLongTerm ? Math.floor(Math.random() * 730) + 366 : Math.floor(Math.random() * 364) + 1;
        const purchaseDate = new Date();
        purchaseDate.setDate(purchaseDate.getDate() - daysAgo);
        
        const quantity = Math.floor(Math.random() * 200) + 10;
        const costBasis = Math.random() * 250 + 50;
        const priceDelta = (Math.random() * 0.4) - 0.2; // -20% to +20% change
        const currentValue = costBasis * (1 + priceDelta) * quantity;
        
        mockTaxLots.push({
          id: `lot_${mockTaxLots.length + 1}`,
          symbol,
          quantity,
          purchaseDate: purchaseDate.toISOString().split('T')[0],
          costBasis: parseFloat(costBasis.toFixed(2)),
          currentValue: parseFloat(currentValue.toFixed(2))
        });
      }
    });
    
    setTaxLots(mockTaxLots);
    
    // Generate mock tax efficiency metrics
    const mockTaxEfficiency: TaxEfficiencyMetrics = {
      potentialLossHarvest: Math.random() * 15000,
      washSaleExposure: Math.random() * 20000,
      STCGExposure: Math.random() * taxConfig.maxSTCG * 1.2, // Sometimes exceeds limit
      LTCGExposure: Math.random() * taxConfig.maxLTCG * 0.9,
      assetLocationScore: Math.random() * 40 + 60, // 60-100 score
      taxDragPercent: Math.random() * 1.5 // 0-1.5% drag on returns
    };
    setTaxEfficiency(mockTaxEfficiency);
    
    // Generate mock tax alerts
    const mockAlerts: TaxAlert[] = [];
    
    // Tax-loss harvesting opportunity
    if (mockTaxEfficiency.potentialLossHarvest > 5000) {
      const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      mockAlerts.push({
        id: 'alert_tlh_1',
        type: 'tax_loss_harvest',
        symbol: randomSymbol,
        severity: 'medium',
        message: `Tax-loss harvesting opportunity in ${randomSymbol} - potential savings of $${mockTaxEfficiency.potentialLossHarvest.toFixed(0)}.`,
        potentialSavings: mockTaxEfficiency.potentialLossHarvest * 0.35, // Assumed tax rate
        recommendation: `Consider selling ${randomSymbol} and replacing with a similar ETF to capture losses while maintaining market exposure.`
      });
    }
    
    // Wash sale warning
    if (mockTaxEfficiency.washSaleExposure > 10000) {
      const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      mockAlerts.push({
        id: 'alert_ws_1',
        type: 'wash_sale',
        symbol: randomSymbol,
        severity: 'high',
        message: `Potential wash sale risk detected for ${randomSymbol} - recent sale at loss followed by purchase within 30-day window.`,
        recommendation: `Avoid purchasing ${randomSymbol} again until after the 30-day window to preserve tax-loss benefit.`
      });
    }
    
    // STCG limit warning
    if (mockTaxEfficiency.STCGExposure > taxConfig.maxSTCG * 0.8) {
      mockAlerts.push({
        id: 'alert_stcg_1',
        type: 'stcg_limit',
        severity: mockTaxEfficiency.STCGExposure > taxConfig.maxSTCG ? 'high' : 'medium',
        message: `Short-term capital gains approaching your annual limit of $${taxConfig.maxSTCG.toLocaleString()}.`,
        recommendation: `Consider deferring additional short-term sales to next tax year or use tax-loss harvesting to offset gains.`
      });
    }
    
    // Asset location optimization
    if (mockTaxEfficiency.assetLocationScore < 80) {
      mockAlerts.push({
        id: 'alert_al_1',
        type: 'asset_location',
        severity: 'low',
        message: `Asset location efficiency can be improved - dividend-paying stocks may be better held in tax-advantaged accounts.`,
        recommendation: `Review our recommended asset location strategy below to optimize tax efficiency.`
      });
    }
    
    // Allocation drift
    if (Math.random() > 0.7) {
      mockAlerts.push({
        id: 'alert_drift_1',
        type: 'drift',
        severity: 'medium',
        message: `Portfolio allocation has drifted from target by more than 5%.`,
        recommendation: `Consider rebalancing using the tax-optimized path suggested below.`
      });
    }
    
    setTaxAlerts(mockAlerts);
    
    // Generate optimal asset location recommendations
    const mockOptimalAllocation: TaxAllocation[] = symbols.map(symbol => {
      // More tax-efficient assets go in taxable accounts, less efficient in tax-advantaged
      const isTaxEfficient = Math.random() > 0.5;
      return {
        symbol,
        taxableAccount: isTaxEfficient ? 70 + Math.random() * 30 : 10 + Math.random() * 30,
        taxDeferred: isTaxEfficient ? 10 + Math.random() * 15 : 40 + Math.random() * 30,
        taxFree: isTaxEfficient ? 5 + Math.random() * 10 : 30 + Math.random() * 20
      };
    });
    
    setOptimalAllocation(mockOptimalAllocation);
    }
    
    // Always set account data (this could also be fetched from real broker data in the future)
    const accounts: AccountType[] = [
      { id: 'acc1', name: 'Taxable Brokerage', type: 'taxable', value: 650000 },
      { id: 'acc2', name: 'Traditional IRA', type: 'tax_deferred', value: 420000 },
      { id: 'acc3', name: 'Roth IRA', type: 'tax_free', value: 380000 }
    ];
    setAccounts(accounts);
  };

  // Handler for selecting a strategy - now uses smart data source selection
  const handleStrategySelect = async (strategyId: string, source: 'internal' | 'external') => {
    console.log(`[PortfolioOverview] 🎯 STRATEGY SELECTED: ${strategyId} (${source})`);
    setSelectedStrategyId(strategyId);
    setIsCalculating(true);

    try {
      // Find the selected strategy for debugging
      const selectedStrategy = source === 'internal' ? 
        internalStrategies.find(s => s.id === strategyId) : 
        externalStrategies.find(s => s.id === strategyId);
        
      console.log(`[PortfolioOverview] Selected strategy details:`, {
        name: selectedStrategy?.name,
        hasStartedTrading: (selectedStrategy as any)?.backtestSettings?.hasStartedTrading,
        vmStrategy: (selectedStrategy as any)?.vmStrategy
      });
      
      // 🎯 Use the new smart data source selection function
      const portfolioData = await getPortfolioDataForStrategy(strategyId, source);
      
      console.log(`[PortfolioOverview] Portfolio data for ${strategyId}:`, {
        dataSource: portfolioData.dataSource,
        totalValue: portfolioData.portfolioSummary.totalValue,
        tradesCount: portfolioData.recentTrades.length,
        riskMetricsCount: portfolioData.riskMetrics.length
      });
      
      // Set the data from the appropriate source
      setSelectedPortfolioSummary(portfolioData.portfolioSummary);
      setSelectedRiskMetrics(portfolioData.riskMetrics);
      setSelectedRecentTrades(portfolioData.recentTrades);
      setSelectedMonthlyPerformance(portfolioData.monthlyPerformance);
      
      // Handle AI insight based on data source
      if (portfolioData.dataSource === 'broker') {
        // For live strategies with real data
        const selectedStrategy = internalStrategies.find(s => s.id === strategyId);
        fetchAIInsight(portfolioData.portfolioSummary, portfolioData.riskMetrics, selectedStrategy);
        
        // Show success message for live strategy data
        console.log(`[PortfolioOverview] Successfully loaded REAL broker data for live strategy: ${strategyId}`);
      } else if (portfolioData.dataSource === 'simulation') {
        // For backtest/paper strategies
        fetchAIInsight(portfolioData.portfolioSummary, portfolioData.riskMetrics);
        
        console.log(`[PortfolioOverview] Successfully loaded simulation data for strategy: ${strategyId}`);
      } else if (portfolioData.dataSource === 'external') {
        // Handle external strategy insights
        const externalStrategy = externalStrategies.find(s => s.id === strategyId);
        
        if (!portfolioData.portfolioSummary) {
          // Error state or pending analysis
          if (externalStrategy?.status === 'pending_analysis') {
            setSelectedAiInsight({
              id: 'pending',
              timestamp: new Date().toISOString(),
              category: 'ExternalStrategy',
              text: `Analysis pending for uploaded ${externalStrategy.fileType?.toUpperCase()} file. Stewie will notify you when results are ready.`,
              severity: 'info'
            });
          } else {
            setSelectedAiInsight({
              id: 'error',
              timestamp: new Date().toISOString(),
              category: 'ExternalStrategy',
              text: 'No trade data available for analysis. Please try uploading a different file.',
              severity: 'warning'
            });
          }
        } else {
          // External strategy with processed data
          fetchAIInsight(portfolioData.portfolioSummary, portfolioData.riskMetrics, externalStrategy);
        }
      }
      
      // Generate tax-related data for all strategies
      generateTaxData();
      
    } catch (err) {
      console.error('Error processing strategy data:', err);
      setSelectedPortfolioSummary(null);
      setSelectedRiskMetrics([]);
      setSelectedRecentTrades([]);
      setSelectedMonthlyPerformance(null);
      setSelectedAiInsight({
        id: 'error',
        timestamp: new Date().toISOString(),
        category: 'ExternalStrategy',
        text: 'Error analyzing strategy data. Please try again or contact support.',
        severity: 'warning'
      });
    } finally {
      setIsCalculating(false);
    }
  };

  // Handler for adding an external strategy
  const handleExternalStrategyAdd = (strategyData: ExternalStrategyData) => {
    console.log('Adding external strategy:', strategyData);
    setExternalStrategies(prev => [...prev, strategyData]);
    
    // Automatically select the newly added strategy
    setSelectedStrategyId(strategyData.id);
    handleStrategySelect(strategyData.id, 'external');
  };
  
  // Handler for selecting a data provider
  const handleProviderSelected = (providerId: DataProviderId) => {
    setSelectedDataProvider(providerId);
    setIsProviderSelectionOpen(false);
    
    console.log(`Selected data provider: ${providerId}`);
    // Future implementation: fetch data from the selected provider
    
    // Trigger a refresh with the new data provider
    fetchData(false);
  };

  // Function to fetch AI insights from Supabase Edge Function
  const fetchAIInsight = async (portfolioData, riskMetrics, strategyData = null) => {
    setAiInsightLoading(true);
    
    try {
      console.log('Requesting AI insight from Supabase Edge Function...');
      
      // Prepare portfolio context for AI analysis
      const portfolioContext = {
        totalValue: portfolioData?.totalValue || 0,
        todayChange: portfolioData?.todayChangeValue || 0,
        todayChangePercent: portfolioData?.todayChangePercent || 0,
        activePositions: brokerPositions.length || 0,
        riskMetrics: riskMetrics.map(metric => ({
          name: metric.name,
          value: metric.value,
          level: metric.level
        })),
        strategyInfo: strategyData ? {
          name: strategyData.name,
          mode: strategyData.backtestSettings?.mode || 'unknown',
          ytdReturn: strategyData.ytdReturn
        } : null,
        taxAlerts: taxAlerts.length,
        hasRealData: brokerPositions.length > 0
      };

      // Create a specialized prompt for portfolio insights
      const portfolioPrompt = `As Stewie, provide a brief portfolio insight based on this data:

Portfolio Value: $${portfolioContext.totalValue.toLocaleString()}
Today's Change: $${portfolioContext.todayChange.toFixed(2)} (${portfolioContext.todayChangePercent.toFixed(2)}%)
Active Positions: ${portfolioContext.activePositions}
${portfolioContext.strategyInfo ? `Selected Strategy: ${portfolioContext.strategyInfo.name} (${portfolioContext.strategyInfo.mode} mode)` : ''}

Risk Metrics:
${portfolioContext.riskMetrics.map(metric => `- ${metric.name}: ${metric.value} (${metric.level} risk)`).join('\n')}

Tax Alerts: ${portfolioContext.taxAlerts} active alerts

Please provide a concise, actionable insight focusing on:
1. Portfolio performance assessment
2. Risk management recommendations  
3. Any notable opportunities or concerns
4. Brief market context if relevant

Keep the response under 150 words and professional but conversational.`;

      // Call Supabase Edge Function
      const response = await supabase.functions.invoke('generate-chat-response', {
        body: {
          prompt: portfolioPrompt,
          history: [] // No chat history needed for portfolio insights
        }
      });

      if (response.data && response.data.content) {
        const aiInsightData: AIInsightData = {
          id: `insight_${Date.now()}`,
          timestamp: new Date().toISOString(),
          category: strategyData ? 'Portfolio' : 'Market',
          text: response.data.content,
          severity: 'info' as const,
          // Add model info - use response data or default to stewie_quant_llama4
          model_used: response.data.model_used || 'stewie_quant_llama4'
        };
        
        setSelectedAiInsight(aiInsightData);
        
        // Show success toast if this was for a strategy selection
        if (strategyData) {
          toast({
            title: "AI Analysis Complete",
            description: "Portfolio insight generated by Stewie.",
          });
        }
        
        return;
      } else {
        console.error('Invalid response from Supabase Edge Function:', response.data);
      }
      
      // Fall back to mock data
      console.log('Falling back to mock AI insight');
      const fallbackInsight = mockData.generateAIInsight();
      fallbackInsight.text = `${fallbackInsight.text} (Generated using fallback data - AI service temporarily unavailable)`;
      fallbackInsight.model_used = 'fallback_model';
      setSelectedAiInsight(fallbackInsight);
      
      // Show a toast notification about the fallback
      toast({
        title: "Using Fallback Insights",
        description: "AI service is temporarily unavailable. Using simulated insights.",
        variant: "destructive",
      });
    } catch (error) {
      console.error('Error fetching AI insight from Supabase:', error);
      
      // If Supabase fails, fall back to mock data with a note about the fallback
      const fallbackInsight = mockData.generateAIInsight();
      fallbackInsight.text = `${fallbackInsight.text} (Generated using fallback data - AI service temporarily unavailable)`;
      fallbackInsight.model_used = 'fallback_model';
      setSelectedAiInsight(fallbackInsight);
      
      // Show a toast notification about the fallback
      toast({
        title: "Using Fallback Insights",
        description: "AI service is temporarily unavailable. Using simulated insights.",
        variant: "destructive",
      });
    } finally {
      setAiInsightLoading(false);
    }
  };

  // Function to run tax efficiency analysis
  const runTaxEfficiencyAnalysis = async () => {
    setAnalysisInProgress(true);
    
    try {
      // In a real implementation, we would call an API endpoint
      // For demo purposes, we'll simulate processing time and reuse existing data
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate new tax alerts
      const updatedAlerts = [...taxAlerts];
      
      // Add a new tax-loss harvesting alert if none exists
      if (!updatedAlerts.some(alert => alert.type === 'tax_loss_harvest')) {
        const lotWithLoss = taxLots.find(lot => lot.currentValue < lot.costBasis * lot.quantity);
        if (lotWithLoss) {
          const potentialSavings = (lotWithLoss.costBasis * lotWithLoss.quantity - lotWithLoss.currentValue) * 0.35;
          updatedAlerts.push({
            id: `alert_tlh_${Date.now()}`,
            type: 'tax_loss_harvest',
            symbol: lotWithLoss.symbol,
            severity: 'medium',
            message: `New tax-loss harvesting opportunity in ${lotWithLoss.symbol}.`,
            potentialSavings,
            recommendation: `Sell ${lotWithLoss.symbol} to realize $${potentialSavings.toFixed(0)} in tax savings.`
          });
        }
      }
      
      setTaxAlerts(updatedAlerts);
      
      // Update tax efficiency metrics with slightly improved values
      if (taxEfficiency) {
        setTaxEfficiency({
          ...taxEfficiency,
          potentialLossHarvest: taxEfficiency.potentialLossHarvest + 2000,
          assetLocationScore: Math.min(100, taxEfficiency.assetLocationScore + 5),
          taxDragPercent: Math.max(0, taxEfficiency.taxDragPercent - 0.2)
        });
      }
      
      // Show success toast
      toast({
        title: "Tax Efficiency Analysis Complete",
        description: "New tax-saving opportunities have been identified.",
      });
    } catch (error) {
      console.error('Error running tax analysis:', error);
      toast({
        title: "Analysis Failed",
        description: "Unable to complete tax efficiency analysis. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAnalysisInProgress(false);
    }
  };
  
  // Function to check tax compliance with LLM
  const checkTaxCompliance = async () => {
    setComplianceCheckInProgress(true);
    
    try {
      // In a real implementation, we would call the compliance AI endpoint
      // For now, we'll simulate the API call
      await new Promise(resolve => setTimeout(resolve, 2500));
      
      // Prepare sample data for what would be sent to the compliance service
      const complianceData = {
        portfolio: {
          value: selectedPortfolioSummary?.totalValue || 0,
          taxLots,
          recentTrades: selectedRecentTrades
        },
        taxConfig,
        taxMetrics: taxEfficiency
      };
      
      console.log('Sending to compliance check:', complianceData);
      
      // Simulate response - in reality this would come from the LLM
      const mockComplianceResponse = `
Compliance Check Results:

Based on the provided portfolio and tax data, I've analyzed for compliance with wash sale rules and your defined capital gains limits.

1. Wash Sale Compliance:
   ✓ No immediate wash sale violations detected
   ⚠️ CAUTION: Recent sale of AAPL (5 days ago) - avoid repurchasing until 25 days from now

2. Capital Gains Limits:
   ✓ Current LTCG: $${(taxEfficiency?.LTCGExposure || 0).toFixed(0)} (within your $${taxConfig.maxLTCG.toLocaleString()} limit)
   ⚠️ Current STCG: $${(taxEfficiency?.STCGExposure || 0).toFixed(0)} (${taxEfficiency?.STCGExposure && taxEfficiency.STCGExposure > taxConfig.maxSTCG ? 'EXCEEDS' : 'approaching'} your $${taxConfig.maxSTCG.toLocaleString()} limit)

3. Tax Optimization Recommendations:
   • Consider harvesting losses in MSFT to offset recent gains
   • Review asset location optimization suggestions
   • Defer any non-essential AAPL sales until next tax year
   
The proposed tax-loss harvesting would reduce your net capital gains by approximately $${((taxEfficiency?.potentialLossHarvest || 0) * 0.35).toFixed(0)}, maintaining compliance with your annual capital gains limits.
      `;
      
      setComplianceResult(mockComplianceResponse);
      
      toast({
        title: "Compliance Check Complete",
        description: "Tax compliance analysis completed successfully.",
      });
    } catch (error) {
      console.error('Error in compliance check:', error);
      toast({
        title: "Compliance Check Failed",
        description: "Unable to complete compliance check. Please try again.",
        variant: "destructive",
      });
    } finally {
      setComplianceCheckInProgress(false);
    }
  };

  // Use useEffect to call fetchData when the component mounts
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add debug utility to global scope for testing (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).debugPortfolioOverview = {
        internalStrategies,
        selectedStrategyId,
        selectedPortfolioSummary,
        selectedRiskMetrics,
        brokerPositions,
        strategyOrders,
        testStrategySelect: (strategyId: string) => {
          console.log('🧪 Testing strategy selection:', strategyId);
          handleStrategySelect(strategyId, 'internal');
        },
        refreshData: () => {
          console.log('🔄 Refreshing portfolio data...');
          fetchData(false);
        },
        analyzeStrategy: (strategyId: string) => {
          const strategy = internalStrategies.find(s => s.id === strategyId);
          if (strategy) {
            console.log('🔍 Strategy Analysis:', {
              id: strategy.id,
              name: strategy.name,
              hasStartedTrading: (strategy as any).backtestSettings?.hasStartedTrading,
              persistentState: strategyTradingStates[strategyId],
              vmStrategy: (strategy as any).vmStrategy,
              backtestSettings: (strategy as any).backtestSettings
            });
          } else {
            console.log('❌ Strategy not found:', strategyId);
          }
        },
        resetStrategyState: (strategyId: string) => {
          console.log('🔄 Resetting strategy state for:', strategyId);
          setStrategyTradingStates(prev => {
            const newState = { ...prev };
            delete newState[strategyId];
            return newState;
          });
        },
        forceEmptyState: (strategyId: string) => {
          console.log('🎯 Forcing empty state for strategy:', strategyId);
          setStrategyTradingStates(prev => ({ ...prev, [strategyId]: false }));
        },
        checkConsistency: () => {
          console.log('🔍 Checking strategy state consistency:');
          internalStrategies.forEach(s => {
            const hasStartedTrading = (s as any).backtestSettings?.hasStartedTrading;
            const persistentState = strategyTradingStates[s.id];
            const isVMStrategy = !!(s as any).vmStrategy;
            console.log(`Strategy ${s.id}:`, {
              name: s.name,
              hasStartedTrading,
              persistentState,
              isVMStrategy,
              consistent: hasStartedTrading === persistentState
            });
          });
        }
      };
    }
  }, [internalStrategies, selectedStrategyId, selectedPortfolioSummary, strategyTradingStates]);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
          <span className="text-lg">Loading portfolio data...</span>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center text-red-700">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <div>
                <h3 className="font-semibold">Error Loading Portfolio</h3>
                <p className="text-sm">{error}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchData()} 
                  className="mt-2"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {!isLoading && !error && (
        <>
          {/* Header with Strategy Selector */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Portfolio Overview</h1>
              <p className="text-gray-600 mt-1">Monitor your trading strategies and tax efficiency</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2"
              >
                <UploadCloud className="h-4 w-4" />
                Upload Strategy
              </Button>
              <Button
                variant="outline"
                onClick={() => fetchData(false)}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Strategy Selector */}
          {allStrategies.length > 0 && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Strategy Selection
                  {brokerBalance && (
                    <Badge variant="secondary" className="ml-2">
                      Live: ${brokerBalance.portfolio_value ? Number(brokerBalance.portfolio_value).toLocaleString() : '0'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StrategySelectorList
                  strategies={allStrategies}
                  selectedStrategyId={selectedStrategyId}
                  onSelectStrategy={handleStrategySelect}
                  isExpanded={isExpanded}
                />
              </CardContent>
            </Card>
          )}

          {/* Main Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview">Portfolio Overview</TabsTrigger>
              <TabsTrigger value="tax_efficiency">Tax Efficiency</TabsTrigger>
            </TabsList>

            {/* Portfolio Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Data Source Indicator (Development Mode) */}
              {process.env.NODE_ENV === 'development' && selectedStrategyId && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        DEBUG
                      </Badge>
                      <span className="text-sm text-gray-600">
                        Strategy: <strong>{internalStrategies.find(s => s.id === selectedStrategyId)?.name || 'Unknown'}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant={
                          selectedPortfolioSummary?.totalValue === 0 ? 'secondary' :
                          internalStrategies.find(s => s.id === selectedStrategyId)?.name === "Demo Strategy (Sample)" ? 'outline' :
                          'default'
                        }
                        className="text-xs"
                      >
                        {selectedPortfolioSummary?.totalValue === 0 ? 'EMPTY STATE' :
                         internalStrategies.find(s => s.id === selectedStrategyId)?.name === "Demo Strategy (Sample)" ? 'DEMO DATA' :
                         'REAL DATA'}
                      </Badge>
                      <Badge 
                        variant={
                          (internalStrategies.find(s => s.id === selectedStrategyId) as any)?.backtestSettings?.hasStartedTrading ? 'default' : 'secondary'
                        }
                        className="text-xs"
                      >
                        {(internalStrategies.find(s => s.id === selectedStrategyId) as any)?.backtestSettings?.hasStartedTrading ? 'TRADING' : 'DEPLOYED'}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Portfolio Summary Cards */}
              {selectedPortfolioSummary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">${selectedPortfolioSummary.totalValue.toLocaleString()}</div>
                      <p className="text-sm text-gray-600">Total Portfolio Value</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className={`text-2xl font-bold ${selectedPortfolioSummary.todayChangeValue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${selectedPortfolioSummary.todayChangeValue.toLocaleString()}
                      </div>
                      <p className="text-sm text-gray-600">Today's Change</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className={`text-2xl font-bold ${selectedPortfolioSummary.todayChangePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedPortfolioSummary.todayChangePercent.toFixed(2)}%
                      </div>
                      <p className="text-sm text-gray-600">Today's Change %</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">
                        {selectedRiskMetrics.find(m => m.id === 'strategy-positions')?.value || 
                         selectedRiskMetrics.find(m => m.id === 'positions-count')?.value || 
                         '0'}
                      </div>
                      <p className="text-sm text-gray-600">Strategy Positions</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Performance Chart */}
              {selectedPortfolioSummary && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Performance Chart</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedPortfolioSummary.totalValue === 0 && selectedPortfolioSummary.equityCurve.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-64 text-center">
                        <BarChart3 className="h-16 w-16 text-blue-300 mb-4" />
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">Strategy Deployed & Ready</h3>
                        <p className="text-gray-500 max-w-md">
                          Your strategy is successfully deployed and monitoring the markets. 
                          Performance charts will appear here once trading begins.
                        </p>
                        <div className="mt-4 px-4 py-2 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-700">
                            ✓ Strategy is active and waiting for optimal entry signals
                          </p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => fetchData(false)} 
                          className="mt-4 flex items-center gap-2"
                          disabled={isLoading}
                        >
                          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                          Check for Updates
                        </Button>
                      </div>
                    ) : (
                      <PortfolioPerformanceChart 
                        summaryData={selectedPortfolioSummary}
                        loading={isLoading}
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Risk Overview and Recent Trades */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Risk Overview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Risk Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RiskOverview 
                      metrics={selectedRiskMetrics} 
                      loading={isLoading}
                    />
                  </CardContent>
                </Card>

                {/* Recent Trades */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Trades</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <RecentTradesTable 
                      trades={selectedRecentTrades}
                      loading={isLoading}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* AI Insights */}
              {selectedAiInsight && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      AI Portfolio Insights
                      {aiInsightLoading && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AIInsightCard 
                      insight={selectedAiInsight}
                      loading={aiInsightLoading}
                      onRefreshInsight={() => fetchAIInsight(selectedPortfolioSummary, selectedRiskMetrics)}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Monthly Review */}
              {selectedMonthlyPerformance && (
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Performance Review</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <MonthlyReviewSection 
                      monthlyData={selectedMonthlyPerformance}
                      loading={isLoading}
                    />
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Tax Efficiency Tab */}
            <TabsContent value="tax_efficiency" className="space-y-6">
              {/* Tax Efficiency Summary */}
              {taxEfficiency && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold text-green-600">
                        ${taxEfficiency.potentialLossHarvest.toLocaleString()}
                      </div>
                      <p className="text-sm text-gray-600">Potential Tax Loss Harvest</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold">
                        {taxEfficiency.assetLocationScore.toFixed(0)}%
                      </div>
                      <p className="text-sm text-gray-600">Asset Location Score</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-2xl font-bold text-red-600">
                        {taxEfficiency.taxDragPercent.toFixed(2)}%
                      </div>
                      <p className="text-sm text-gray-600">Annual Tax Drag</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tax Alerts */}
              {taxAlerts.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Tax Alerts & Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {taxAlerts.map((alert) => (
                      <div 
                        key={alert.id} 
                        className={`p-4 rounded-lg border ${
                          alert.severity === 'high' ? 'border-red-200 bg-red-50' :
                          alert.severity === 'medium' ? 'border-yellow-200 bg-yellow-50' :
                          'border-blue-200 bg-blue-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`rounded-full p-1 ${
                            alert.severity === 'high' ? 'bg-red-100' :
                            alert.severity === 'medium' ? 'bg-yellow-100' :
                            'bg-blue-100'
                          }`}>
                            <AlertTriangle className={`h-4 w-4 ${
                              alert.severity === 'high' ? 'text-red-600' :
                              alert.severity === 'medium' ? 'text-yellow-600' :
                              'text-blue-600'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {alert.type.replace('_', ' ').toUpperCase()}
                              </Badge>
                              {alert.symbol && (
                                <Badge variant="secondary" className="text-xs">
                                  {alert.symbol}
                                </Badge>
                              )}
                              {alert.potentialSavings && (
                                <Badge variant="outline" className="text-xs text-green-600">
                                  Save ${alert.potentialSavings.toFixed(0)}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium mb-1">{alert.message}</p>
                            {alert.recommendation && (
                              <p className="text-xs text-gray-600">{alert.recommendation}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Tax Analysis Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Tax Efficiency Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Run a comprehensive tax efficiency analysis to identify optimization opportunities.
                    </p>
                    <Button 
                      onClick={runTaxEfficiencyAnalysis}
                      disabled={analysisInProgress}
                      className="w-full"
                    >
                      {analysisInProgress ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Analyzing...
                        </>
                      ) : (
                        'Run Tax Analysis'
                      )}
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="h-5 w-5" />
                      Compliance Check
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Verify compliance with wash sale rules and capital gains limits.
                    </p>
                    <Button 
                      onClick={checkTaxCompliance}
                      disabled={complianceCheckInProgress}
                      variant="outline"
                      className="w-full"
                    >
                      {complianceCheckInProgress ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Checking...
                        </>
                      ) : (
                        'Check Compliance'
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Compliance Results */}
              {complianceResult && (
                <Card>
                  <CardHeader>
                    <CardTitle>Compliance Analysis Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-sm whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                      {complianceResult}
                    </pre>
                  </CardContent>
                </Card>
              )}

              {/* Tax Lots Table */}
              {taxLots.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Tax Lots</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Symbol</th>
                            <th className="text-left p-2">Quantity</th>
                            <th className="text-left p-2">Purchase Date</th>
                            <th className="text-left p-2">Cost Basis</th>
                            <th className="text-left p-2">Current Value</th>
                            <th className="text-left p-2">Gain/Loss</th>
                            <th className="text-left p-2">Term</th>
                          </tr>
                        </thead>
                        <tbody>
                          {taxLots.map((lot) => {
                            const totalCost = lot.costBasis * lot.quantity;
                            const gainLoss = lot.currentValue - totalCost;
                            const gainLossPercent = (gainLoss / totalCost) * 100;
                            const isLongTerm = new Date().getTime() - new Date(lot.purchaseDate).getTime() > 365 * 24 * 60 * 60 * 1000;
                            
                            return (
                              <tr key={lot.id} className="border-b hover:bg-gray-50">
                                <td className="p-2 font-medium">{lot.symbol}</td>
                                <td className="p-2">{lot.quantity}</td>
                                <td className="p-2">{lot.purchaseDate}</td>
                                <td className="p-2">${lot.costBasis.toFixed(2)}</td>
                                <td className="p-2">${lot.currentValue.toLocaleString()}</td>
                                <td className={`p-2 ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  ${gainLoss.toFixed(0)} ({gainLossPercent.toFixed(1)}%)
                                </td>
                                <td className="p-2">
                                  <Badge variant={isLongTerm ? "secondary" : "outline"}>
                                    {isLongTerm ? 'Long-term' : 'Short-term'}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* External Strategy Upload Modal */}
      <ExternalStrategyUploadModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onStrategyAdd={handleExternalStrategyAdd}
      />

      {/* Data Provider Selection Dialog */}
      <DataProviderSelectionDialog
        open={isProviderSelectionOpen}
        onOpenChange={setIsProviderSelectionOpen}
        onProviderSelect={handleProviderSelected}
      />
    </div>
  );
};